/**
 * MSAL Configuration for Azure AD Authentication
 * 
 * This module contains the configuration for Microsoft Authentication Library (MSAL)
 * including client configuration, scopes, and helper functions.
 */

/**
 * MSAL configuration object for PublicClientApplication
 */
export const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_CLIENT_ID,
        authority: `https://login.microsoftonline.com/${process.env.REACT_APP_TENANT_ID}`,
        redirectUri: process.env.REACT_APP_REDIRECT_URI,
        postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI,
        navigateToLoginRequestUrl: true
    },
    cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) return;
                switch (level) {
                    case 0: // Error
                        console.error(message);
                        break;
                    case 1: // Warning
                        console.warn(message);
                        break;
                    case 2: // Info
                        console.info(message);
                        break;
                    case 3: // Verbose
                        console.debug(message);
                        break;
                    default:
                        break;
                }
            },
            logLevel: 1 // Warning level
        }
    }
};

/**
 * Scopes for login request - basic user profile information only
 * API scope is requested separately when making API calls
 */
export const loginRequest = {
    scopes: ["openid", "profile", "email"]
};

/**
 * Scopes for API requests - includes custom backend API scope
 */
export const apiRequest = {
    scopes: [process.env.REACT_APP_API_SCOPE]
};



/**
 * Extract user ID from MSAL account
 * Returns the username portion before @ for enterprise users,
 * or the full username if no @ is present
 * @param {object} account - MSAL account object
 * @returns {string|null} - User ID or null if not available
 */
export function getUserId(account) {
    if (!account) return null;
    
    // Try to get from username (typically user@domain.com)
    const username = account.username;
    console.log("Extracting user ID from account:", account);
    if (username && username.includes("@")) {
        return username.split("@")[0].toUpperCase();
    }
    
    // Fallback to localAccountId or homeAccountId
    return account.localAccountId || account.homeAccountId || username || null;
}

/**
 * Extract display name from MSAL account
 * @param {object} account - MSAL account object
 * @returns {string|null} - Display name or null if not available
 */
export function getUserName(account) {
    if (!account) return null;
    
    // Prefer the name claim
    if (account.name) {
        return account.name;
    }
    
    // Fallback to username
    return account.username || null;
}

/**
 * Extract app roles from MSAL account
 * App roles are defined in Azure AD App Registration and assigned to users/groups
 * @param {object} account - MSAL account object
 * @returns {string[]} - Array of app role values assigned to the user
 */
export function getUserRoles(account) {
    if (!account) return [];
    
    // Roles are stored in idTokenClaims.roles
    const roles = account.idTokenClaims?.roles;
    
    if (Array.isArray(roles)) {
        return roles.map(role => role.toLowerCase());
    }
    
    return [];
}

/**
 * Check if user has a specific app role
 * @param {object} account - MSAL account object
 * @param {string} role - Role value to check (e.g., 'Task.admin', 'Task.User')
 * @returns {boolean} - True if user has the specified role
 */
export function hasRole(account, role) {
    const roles = getUserRoles(account);
    return roles.includes(role.toLowerCase());
}

/**
 * Check if user is an admin
 * @param {object} account - MSAL account object
 * @returns {boolean} - True if user has admin role
 */
export function isAdmin(account) {
    return hasRole(account, 'Task.admin');
}
